import { useState, useEffect } from 'react'
import { ChevronLeft } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

// Import all images
import coinIcon from '../assets/images/allocation/coin.png'
import qrCode from '../assets/images/allocation/pinai_product_qr.png'
import step2Screenshot from '../assets/images/allocation/step2.png'
import step3Screenshot from '../assets/images/allocation/step3.png'
import step4Screenshot from '../assets/images/allocation/step4.png'

export function AllocationHelpPage() {
  const navigate = useNavigate()
  const [isCopied, setIsCopied] = useState(false)

  const handleCopyLink = async () => {
    const textToCopy = 'https://www.pinai.io/product'
    
    try {
      // 优先尝试现代的 Clipboard API
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(textToCopy)
        setIsCopied(true)
        return
      }
      
      // 备用方案：使用传统方法（适用于币安App WebView）
      const textArea = document.createElement('textarea')
      textArea.value = textToCopy
      textArea.style.position = 'fixed'
      textArea.style.left = '-999999px'
      textArea.style.top = '-999999px'
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()
      
      const successful = document.execCommand('copy')
      document.body.removeChild(textArea)
      
      if (successful) {
        setIsCopied(true)
      } else {
        throw new Error('Copy command failed')
      }
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  useEffect(() => {
    if (isCopied) {
      const timer = setTimeout(() => {
        setIsCopied(false)
      }, 5000)
      
      return () => clearTimeout(timer)
    }
  }, [isCopied])

  return (
    <div className="w-full min-h-screen bg-[#F2FBFE]">
      <div className="flex flex-col min-h-screen">
        {/* Header */}
        <div className="flex-shrink-0 h-[60px] flex items-center px-[15px] mt-[10px]">
          <button 
            onClick={() => navigate(-1)}
            className="w-[30px] h-[25.71px] flex items-center justify-center"
          >
            <ChevronLeft className="w-6 h-6 text-gray-900" strokeWidth={2.5} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 px-[15px] pb-[34px] mt-[-20px]">
          <div className="flex flex-col gap-2.5 w-full max-w-[349px] mx-auto">
            {/* Title Section */}
            <div className="flex flex-col items-center justify-center gap-2.5 w-full">
              <div className="w-[60px] h-[60px] flex items-center justify-center">
                <img 
                  src={coinIcon} 
                  alt="Coin" 
                  className="w-[60px] h-[60px]"
                  style={{ aspectRatio: '1/1' }}
                />
              </div>
              <h1 className="font-inter font-bold text-black text-[25px] leading-[30px] text-center">
                Activate Your PIN AI Miner to Start Earning
              </h1>
            </div>

            {/* Description */}
            <p className="font-inter font-medium text-gray-900 text-base leading-6 text-left">
              🎯 <b>You've successfully subscribed to the PIN AI Pro Plan!</b>
              <br />
              Follow these 4 simple steps to activate your AI miner and start earning rewards.
              <br />
              The earlier you start, the more you can earn.
            </p>

            {/* Step 1 */}
            <p className="font-inter font-medium text-gray-900 text-base leading-6 text-left">
              <span className="font-bold">1. Download the PIN AI mobile app:</span>
              <br/>
              Available on both the App Store and Google Play. 
              <br/>
              👉 Copy and open this link in your browser: https://www.pinai.io/product  
            </p>
            

            {/* Connect Link Button */}
            <div className="flex flex-col gap-2.5 items-center">
              <button 
                className={`all-[unset] box-border flex w-[200px] items-center justify-center gap-2.5 pl-2.5 pr-6 py-3 relative rounded-xl overflow-hidden shadow-[0px_2px_3px_#cbcccf26] transition-all duration-300 ${
                  isCopied 
                    ? 'bg-black  scale-105' 
                    : 'bg-black hover:bg-gray-800 active:scale-95'
                }`}
                onClick={handleCopyLink}
                disabled={isCopied}
              >
                <div className="relative w-fit font-sf-pro font-normal text-white text-sm tracking-[0] leading-[normal]">
                  {isCopied ? 'Link Copied!' : 'Copy Link'}
                </div>
              </button>
            </div>

            <p className="font-inter font-bold text-gray-900 text-base leading-6 text-left">
              Or simply scan the QR code below:
            </p>
            <div 
              className="w-[200px] h-[200px] mx-auto rounded-[20px]"
              style={{
                aspectRatio: '1/1',
                background: `url(${qrCode}) lightgray 50% / cover no-repeat`
              }}
            />

           

            {/* Step 2 */}
            <p className="font-inter font-medium text-gray-900 text-base leading-6 text-left">
              <b>2. Connect Binance Wallet:</b>
              <br />
              You must connect your <b>Binance Wallet</b> to activate mining.
              <br />
              In the app, navigate to "Me {">"} Connect Wallet" and connect your Binance Wallet to get your PIN AI airdrop.
            </p>

            {/* Screenshot */}
            <div className="w-[200px] h-[359px] rounded-xl overflow-hidden mx-auto">
              <img 
                src={step2Screenshot} 
                alt="Score Screenshot" 
                className="w-full h-full object-cover"
              />
            </div>

            {/* Step 3 */}
            <p className="font-inter font-medium text-gray-900 text-base leading-6 text-left">
              <b>3. Connect your data to power your AI miner:</b>
              <br />
              We strongly recommend also connecting:
              <ul className="list-disc list-inside">
                <li><b> X (Twitter)</b> — for social insights</li>
                <li><b>Gmail & Google Calendar</b> — for your daily life</li>
              </ul>
              <b>PIN AI community:</b> Connecting Telegram and Discord can unlock bonus rewards if you're a Hi PIN or PIN AI community user.
            </p>

            {/* Screenshot */}
            <div className="w-[200px] h-[414px] rounded-xl overflow-hidden mx-auto">
              <img 
                src={step3Screenshot} 
                alt="Score Screenshot" 
                className="w-full h-full object-cover"
              />
            </div>

            {/* Step 4 */}
            <p className="font-inter font-medium text-gray-900 text-base leading-6 text-left">
              <b>3. Run the God Model to get your AI Score:</b>
              <br />
              Go to the <b>Score</b> section and run the God Model.
              <br />
              Your <b>AI Score</b> reflects how well your Personal AI understands you — and determines your mining power.
            </p>

            {/* Screenshot */}
            <div className="w-[200px] h-[408px] rounded-xl overflow-hidden mx-auto">
              <img 
                src={step4Screenshot} 
                alt="Score Screenshot" 
                className="w-full h-full object-cover"
              />
            </div>

            {/* Step 5 */}
            <p className="font-inter font-bold text-gray-900 text-base leading-6 text-left">
              4. Complete weekly tasks and share on X (Twitter):
            </p>

            {/* Step 5 Description */}
            <p className="font-inter text-gray-900 text-base leading-[26px] text-left">
              <b>Boost your AI Score each week by:</b>
              <ul className="list-disc list-inside">
                <li>Completing personalized tasks</li>
                <li>Sharing on X (Twitter) to join the weekly campaign</li>
              </ul>
              Completing weekly tasks boosts your score for the week. Sharing on X brings more invited users, whose participation further increases your score, helping you earn a larger mining share.
            </p>

            {/* Referral Bonus */}
            <p className="font-inter text-gray-900 text-base leading-6 text-left">
              💡 <b>Referral Bonus:</b>
              <br />
              Invite others to subscribe using your referral link.
              <br />
              You'll receive <b>4% of their AI Score</b> as bonus mining power.
            </p>

            {/* Note */}
            <p className="font-inter text-gray-900 text-base leading-6 text-left">
              🔔 <b>Kindly note:</b>
              <br />
              As a subscriber, you receive <b>monthly AI Credits</b> to access PIN AI services and interact with your Personal AI.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
} 